/**
 * Live-Runde 13 item 4: the started „Bestellung anlegen" survives a look at another page. One draft
 * per own login and customer in localStorage, stamped with the Vienna day — a draft of an earlier
 * day is gone (all drafts end at 0:00). Blocked storage falls back to this page load's memory.
 */
const PREFIX = 'lamante.ap.orderDraft.';

interface StoredDraft<T> {
    day: string;
    savedAt: string;
    draft: T;
}

const memory = new Map<string, string>();

const VIENNA_DAY = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Vienna',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

/** YYYY-MM-DD of the Vienna clock. */
export const viennaDay = (now: Date = new Date()): string => VIENNA_DAY.format(now);

export const orderDraftKey = (owner: string, customerId: number): string => `${PREFIX}${owner}.${customerId}`;

function read(key: string): string | null {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return memory.get(key) ?? null;
    }
}

function write(key: string, value: string): void {
    memory.set(key, value);
    try {
        window.localStorage.setItem(key, value);
    } catch {
        // blocked or full storage: the memory copy serves this page load
    }
}

function remove(key: string): void {
    memory.delete(key);
    try {
        window.localStorage.removeItem(key);
    } catch {
        // blocked storage: nothing stored there
    }
}

function ownKeys(owner: string): string[] {
    const prefix = `${PREFIX}${owner}.`;
    const keys = new Set(Array.from(memory.keys()).filter(key => key.startsWith(prefix)));
    try {
        for (let index = 0; index < window.localStorage.length; index++) {
            const key = window.localStorage.key(index);
            if (key?.startsWith(prefix)) keys.add(key);
        }
    } catch {
        // blocked storage: the memory keys only
    }
    return Array.from(keys);
}

function parse<T>(stored: string | null): StoredDraft<T> | null {
    if (stored === null) return null;
    try {
        const parsed = JSON.parse(stored);
        return typeof parsed?.day === 'string' && parsed.draft ? parsed : null;
    } catch {
        return null;
    }
}

export function saveOrderDraft<T>(owner: string, customerId: number, draft: T, now: Date = new Date()): void {
    write(orderDraftKey(owner, customerId), JSON.stringify({ day: viennaDay(now), savedAt: now.toISOString(), draft }));
}

/** Today's draft; a draft of an earlier day is deleted. */
export function loadOrderDraft<T>(owner: string, customerId: number, now: Date = new Date()): T | null {
    const key = orderDraftKey(owner, customerId);
    const stored = parse<T>(read(key));
    if (stored === null || stored.day !== viennaDay(now)) {
        remove(key);
        return null;
    }
    return stored.draft;
}

export const clearOrderDraft = (owner: string, customerId: number): void => remove(orderDraftKey(owner, customerId));

/** Deletes the own drafts of earlier days (panel start). */
export function sweepOrderDrafts(owner: string, now: Date = new Date()): void {
    const today = viennaDay(now);
    ownKeys(owner)
        .filter(key => parse(read(key))?.day !== today)
        .forEach(remove);
}

/** Logout: every own draft. */
export const clearOrderDrafts = (owner: string): void => ownKeys(owner).forEach(remove);
